import './campDetails.css';
import DetailsImageDisplay from '../../components/detailsImageDisplay/DetailsImageDisplay';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactLoading from 'react-loading';
import moment from 'moment';
import ReviewStars from '../../components/reviewStars/ReviewStars';
import { ReactComponent as Share } from '../../assets/icons/trvto_share.svg';
import { share } from './campDetailsFun/tripDetailsShare';
import fetchTripCheck from './tripCheckFun/tripCheck';
import { userReview } from '../../api/userAPI';
import { UserAuthContext } from '../../context/UserAuthContext';
import { ReactComponent as CloseIcon } from '../../assets/icons/trvto_close.svg';
import EmptyPage from '../../components/empty-page/emptyPage';
import { getFormattedDate } from '../../utils/formatDate';
import { handleWishlistClick } from '../../utils/wishlistHandler';
import { fetchWishlistData } from '../../components/tripCard/getUserWishlist/getUserWishlist';
import { UserLoginSecContext } from '../../context/UserLoginSecContext';
import { ReactComponent as LoveBlack } from '../../assets/icons/trvto_wishlist.svg';
import { ReactComponent as LoveRed } from '../../assets/icons/trvto_favorite.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import TripCard from '../../components/tripCard/TripCard';
// import { ReactComponent as LeftArrow } from '../../assets/icons/trvto_left_arrow_icon.svg';
// import { ReactComponent as RightArraow } from '../../assets/icons/trvto_right_arrow_icon.svg';
import { getInterestedTrips, getInterestedTripsOrg } from '../../api/infoAPI';
import { clearBookingStorage } from '../../utils/clearStorage';
import { use } from 'react';
import CampBookingTab from './campBookingTab/CampBookingTab';
import fetchTripData from './campDetailsFun/fetchTripData';
import AccommodationTabBar, { ACCOMMODATION_TABS } from '../../components/accommodationTabBar/AccommodationTabBar';
import AccommodationSectionContent from '../../components/accommodationSections/AccommodationSections';
import {
    BedDouble,
    CalendarDays,
    CheckCircle2,
    ChevronRight,
    House,
    MapPin,
    Maximize,
    ShieldCheck,
    Users,
    Wifi
} from 'lucide-react';
import { formatLabel } from '../../components/accommodationSections/accommodationFormatters';
import { ReactComponent as RatingStar } from '../../assets/icons/trvto_rating-star.svg';

/*
 * Same tab list/section ids as the support side's read-only accommodation
 * view (SupOrgAccommodationDetails.jsx) - both pages now render the exact
 * same tab content, from the exact same shared AccommodationSectionContent
 * switch fed directly from `packData`, instead of each page building its
 * own view of the data (this page used to go through ReviewSubmit +
 * buildFormDataFromAccommodation, a wizard-oriented component meant for
 * something else, while the support side had its own separate, richer
 * section components - the two had drifted apart). The tab list itself
 * comes from AccommodationTabBar.jsx too, so both pages show the exact
 * same tabs, same order, same labels. Which sections show on this side
 * vs. the support/org side is left as a future filter inside the shared
 * switch, not implemented here. The right-side booking panel
 * (CampBookingTab) still expects the old trip-style packData shape and
 * hasn't been rebuilt for accommodation yet - that's deferred, this page
 * only wires up the left/main details.
 */

const CURRENCY_SYMBOLS = {
    INR: '₹',
    USD: '$',
    AED: 'د.إ',
    EUR: '€',
    GBP: '£'
};

const PRICE_UNIT_LABELS = {
    per_night: 'night',
    per_person: 'person',
    per_unit: 'unit',
    per_bed: 'bed',
    per_package: 'package'
};

export default function CampDetails() {
    const isMobile = window.innerWidth < 768;
    const [packData, setPackData] = useState({});
    const [packDates, setPackDates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [popupVisible, setPopupVisible] = useState(true);
    const [userWishlist, setUserWishlist] = useState([]);
    const [checkTrip, setCheckTrip] = useState(false);
    const [userRating, setUserRating] = useState(0);
    const [popupRating, setPopupRating] = useState(userRating);
    const [typeSel, setTypeSel] = useState('camp');
    const [comment, setComment] = useState('');
    const [isFavourite, setIsFavourite] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState(ACCOMMODATION_TABS[0].id);
    const { isAuthenticated } = useContext(UserAuthContext);
    /*
     * Named routerLocation (not `location`) because this page also needs
     * a plain display string for the accommodation's address - the two
     * were colliding under one `location` name, which meant the header
     * and the sidebar's location card were trying to render this
     * react-router object directly as text and crashing with "Objects
     * are not valid as a React child".
     */
    const routerLocation = useLocation();
    const { setLogButton, setLoginSec } = useContext(UserLoginSecContext);
    const packId = useRef(window.location.pathname.split('/')[3]);
    const type = useRef(
        window.location.pathname.split('/')[1] === 'org'
            ? window.location.pathname.split('/')[2]
            : window.location.pathname.split('/')[1]
    );
    const tripStatus = routerLocation.state?.tripStatus;

    const [allowedDates, setAllowedDates] = useState([]);
    const navigate = useNavigate();
    const [interestedTrips, setInterestedTrips] = useState([]);
    const { id } = JSON.parse(localStorage.getItem('tempOrg')) || {};
    const USER_IDS = ['686f837afe8ded032c47991a', '6892ebe235b0fc3f961f5146'];
    const userId = localStorage.getItem('userId');
    const ratingAverage = Number(packData?.rating?.average) || 0;

    const ratingCount = Number(packData?.rating?.count) || 0;

    const totalUnits = useMemo(() => {
        if (!packData) return 0;

        if (packData.totalUnits !== undefined && packData.totalUnits !== null) {
            return packData.totalUnits;
        }

        return (packData.units || []).reduce((total, unit) => total + Number(unit?.inventory?.totalUnits || 0), 0);
    }, [packData]);

    const amenities = packData?.amenities || {};

    const allAmenities = Object.values(amenities)
        .flat()
        .filter((item) => typeof item === 'string' && item.trim());

    const uniqueAmenities = [...new Set(allAmenities)];

    const visibleAmenities = uniqueAmenities.slice(0, 6);

    useEffect(() => {
        clearBookingStorage();
        setTypeSel((prev) => prev);
        /*
         * This was hardcoded to 'trip' - copy-paste leftover from
         * TripDetails.jsx. It meant this page always fetched trip data
         * regardless of which camp/accommodation it was supposed to be
         * showing, so it never actually worked for camps. 'camp' now
         * matches packInfo.js's "camp" case, which serves the same
         * Accommodation documents the org/support sides use.
         */
        fetchTripData('camp', packId.current, setLoading, setPackData, setPackDates);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchWishlistData(typeSel, setUserWishlist);
        fetchTripCheck(type.current, packId.current, setCheckTrip);
    }, [typeSel, isAuthenticated]);

    useEffect(() => {
        // no early return on an empty list: removing the last saved stay must clear the heart too
        setIsFavourite(Boolean(packData?._id) && (userWishlist || []).includes(packData._id));
    }, [userWishlist, packData?._id]);

    useEffect(() => {
        if (packDates.length !== 0) {
            setAllowedDates(
                packDates.map(function (obj) {
                    return moment(obj.startDate);
                })
            );
        }
    }, [packDates]);

    const handleSubmitRating = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const ratingData = {
            packId: packId.current,
            userName: localStorage.getItem('name'),
            userId: localStorage.getItem('userId'),
            rating: popupRating,
            organizerId: packData.organizerId,
            comment
        };
        await userReview(typeSel, ratingData);
        setUserRating(popupRating);
    };

    const handleWishlist = () => {
        if (isAuthenticated) {
            handleWishlistClick(packData._id, userWishlist, typeSel, setUserWishlist);
        } else {
            setLogButton(true);
            setLoginSec('login');
        }
    };

    useEffect(() => {
        if (!type || !packId) {
            return;
        }
        const fetchIntrestedTrips = async () => {
            try {
                const result = id
                    ? await getInterestedTripsOrg(type.current, id)
                    : await getInterestedTrips(type.current, packId.current);
                if (result?.data) {
                    const tripData = result?.data || [];
                    setInterestedTrips(tripData);
                }
            } catch (error) {
                console.error('Error fetching interested trips:', error);
            }
        };
        fetchIntrestedTrips();
    }, [type, packId, id]);

    const carouselRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollButton = () => {
        const el = carouselRef.current;
        if (!el) return;

        const scrollLeft = Math.round(el.scrollLeft);
        const maxScrollLeft = el.scrollWidth - el.clientWidth;

        const canScrollLeftNow = scrollLeft > 0;
        const canScrollRightNow = scrollLeft < maxScrollLeft - 1;

        setCanScrollLeft(canScrollLeftNow);
        setCanScrollRight(canScrollRightNow);
    };

    const scrollLeft = () => {
        const el = carouselRef.current;
        if (!el) return;
        const scrollAmount = el.clientWidth;
        el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        setTimeout(updateScrollButton, 300);
    };

    const scrollRight = () => {
        const el = carouselRef.current;
        if (!el) return;
        const scrollAmount = el.clientWidth;
        el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        setTimeout(updateScrollButton, 300);
    };

    useEffect(() => {
        const scrollEl = carouselRef.current;
        if (!scrollEl) return;

        const handleUpdate = () => updateScrollButton();

        scrollEl.addEventListener('scroll', handleUpdate);

        // Run after layout to get correct dimensions
        setTimeout(updateScrollButton, 0);

        return () => scrollEl.removeEventListener('scroll', handleUpdate);
    }, [interestedTrips]);

    /*
     * Accommodation documents don't have `quickView.baseCities` (that was
     * a trip-only field) - location comes from `address` instead, same as
     * the support side's read-only view.
     */

    const galleryImages = packData?.media?.length
        ? [...packData.media]
              .sort((a, b) => (a?.isCover === b?.isCover ? 0 : a?.isCover ? -1 : 1))
              .map((m) => m.url)
              .filter(Boolean)
        : [];

    const firstPricing = packData?.units?.[0]?.pricing?.[0];
    const startingPrice = firstPricing?.actualPrice ?? firstPricing?.basePrice;
    const currencySymbol = CURRENCY_SYMBOLS[firstPricing?.currency] || '';
    const priceUnitLabel = PRICE_UNIT_LABELS[firstPricing?.priceUnit] || 'night';

    const startCityLocation = [packData?.address?.city, packData?.address?.state].filter(Boolean);
    const location = startCityLocation.join(', ');

    const closePopup = () => setPopupVisible(false);

    return (
        <div>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            {Object.keys(packData).length !== 0 ? (
                <div className='tripDetailsLayout'>
                    <div className='tripDetailsMainRow'>
                        {/*
                         * This page now reuses TripDetails.jsx's design wholesale
                         * (tripDetailsLayout > tripDetailsMainRow > tripDetailsLeft /
                         * tripDetailsBookingUser, the same header markup/classes,
                         * the same interested-trips carousel and rating popup) so
                         * it looks and behaves exactly like the trip page. Only the
                         * accommodation-specific content differs: AccommodationTabBar
                         * + AccommodationSectionContent (shared with the support
                         * side's read-only view) in place of trip's DetailsTab, and
                         * CampBookingTab in place of TripBookingTab.
                         */}
                        <div className='tripDetailsLeft'>
                            <div className='tripDetailsHeadingDivUser'>
                                <div className='detailsImageDisplayNameUser'>
                                    <div>
                                        <div className='detailsImageDisplayNameUser'>
                                            <span className='titleTrip'>{packData?.title || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <div className='tripBriefInfo'>
                                                {USER_IDS.includes(userId) && (
                                                    <>
                                                        <span
                                                            className='tripDetailsSubDetails'
                                                            style={{ fontSize: 'small' }}
                                                        >
                                                            By
                                                        </span>
                                                        <span className='tripDetailsSubDetails'>
                                                            {packData?.organizerName}
                                                        </span>
                                                        <span className='tripDetailsDot'></span>
                                                    </>
                                                )}

                                                {packData?.propertyType && (
                                                    <>
                                                        <span className='tripDetailsSubDetails'>
                                                            {formatLabel(packData.propertyType)}
                                                        </span>
                                                        <span className='tripDetailsDot'></span>
                                                    </>
                                                )}

                                                {startingPrice != null && (
                                                    <>
                                                        <span className='tripDetailsSubDetails'>
                                                            {currencySymbol}
                                                            {Number(startingPrice).toLocaleString('en-IN')} /{' '}
                                                            {priceUnitLabel}
                                                        </span>
                                                        <span className='tripDetailsDot'></span>
                                                    </>
                                                )}

                                                {location && (
                                                    <>
                                                        <span className='tripDetailsSubDetails'>From: {location}</span>
                                                        <span className='tripDetailsDot'></span>
                                                    </>
                                                )}

                                                <span
                                                    className='tripRatings'
                                                    style={{ display: 'flex', alignItems: 'center' }}
                                                >
                                                    <RatingStar style={{ marginRight: '4px' }} />
                                                    <span>{ratingAverage > 0 ? ratingAverage.toFixed(1) : 'New'}</span>
                                                    {ratingCount > 0 && (
                                                        <p style={{ margin: 0 }}>({ratingCount} Reviews)</p>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='icons'>
                                        {isFavourite ? (
                                            <LoveRed className='wishlist-icon-trip-user' onClick={handleWishlist} />
                                        ) : (
                                            <LoveBlack className='wishlist-icon-trip-user' onClick={handleWishlist} />
                                        )}
                                        <Share className='share-icon-trip-user' onClick={(e) => share(e)} />
                                    </div>
                                </div>
                            </div>
                            {galleryImages.length > 0 ? (
                                <DetailsImageDisplay imageData={galleryImages} />
                            ) : (
                                <div className='supAccDetails-noImage'>No accommodation images available</div>
                            )}
                            <div>
                                <AccommodationTabBar activeTab={activeTab} onChange={setActiveTab} sticky />

                                <div className='supAccDetails-tabContent'>
                                    <AccommodationSectionContent activeTab={activeTab} packData={packData} />
                                </div>
                            </div>
                            {interestedTrips && interestedTrips.length > 0 && !isMobile && (
                                <>
                                    <div className='carousel'>
                                        <p className='carousel-header'>You might be interested in</p>
                                    </div>
                                    <div className='carousel-container'>
                                        {canScrollLeft && (
                                            <button className='modalNavButton left' onClick={scrollLeft}>
                                                ‹
                                            </button>
                                        )}
                                        <div className='searchResultContentAreaTrip' ref={carouselRef}>
                                            <TripCard imageData={interestedTrips} interested={true} type={typeSel} />
                                        </div>
                                        {canScrollRight && (
                                            <button className='modalNavButton right' onClick={scrollRight}>
                                                ›
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className='tripDetailsBookingUser'>
                            <CampBookingTab
                                packData={packData}
                                setPackData={setPackData}
                                packDates={packDates}
                                setLoading={setLoading}
                                isExpanded={isExpanded}
                                setIsExpanded={setIsExpanded}
                            />
                        </div>
                    </div>

                    {isMobile && (
                        <>
                            <div className='carousel'>
                                <p className='carousel-header'>You might be interested in</p>
                            </div>
                            <div className='carousel-container'>
                                {canScrollLeft && (
                                    <button className='modalNavButton left' onClick={scrollLeft}>
                                        ‹
                                    </button>
                                )}
                                <div className='searchResultContentAreaTrip' ref={carouselRef}>
                                    <TripCard imageData={interestedTrips} interested={true} type={typeSel} />
                                </div>
                                {canScrollRight && (
                                    <button className='modalNavButton right' onClick={scrollRight}>
                                        ›
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {isAuthenticated &&
                        (tripStatus === 'In Progress' || tripStatus === 'Completed') &&
                        checkTrip?.data?.status &&
                        popupVisible && (
                            <div className='ratingModal'>
                                <div className='modalContent'>
                                    <div className='ratingHeader'>
                                        <h3>Rate Your Previous Trip</h3>
                                        <CloseIcon onClick={closePopup} className='loginCloseIcon' />
                                    </div>
                                    <ReviewStars
                                        rating={popupRating}
                                        onRatingChange={(newRating) => setPopupRating(newRating)}
                                        isInteractive
                                    />
                                    <textarea
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder='Add your comments here...'
                                        className='commentBox'
                                    />
                                    <div className='modalActions'>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSubmitRating(e);
                                            }}
                                            className='submitRating'
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                </div>
            ) : (
                <EmptyPage />
            )}
        </div>
    );
}
